import { useQuery } from "@tanstack/react-query";
import { getStockMovements } from "../fetchers/cmmsSparePart";
import { useLocalStorage } from "./useLocalStorage";

/*
 * The stock ledger for one spare part, newest first.
 *
 * Shaped after useToolServiceHistory: the same one-entity-at-a-time read behind the same
 * drawer pattern. No feature-flag or role gate here, unlike that hook — the only mount point
 * is a page that already sits behind CmmsFeatureRoute, and the endpoint is Cmms_Field, which
 * every role that can reach the page already satisfies.
 *
 * `sparePartId` gates the request rather than defaulting to anything. There is no company-wide
 * form of this read, so a null part means "ask nothing", not "ask about everything".
 */
export function useStockMovements(sparePartId) {
  const [companyKey] = useLocalStorage("companyKey", 1);

  const query = useQuery(
    ["cmmsStockMovements", companyKey, sparePartId ?? null],
    () => getStockMovements(companyKey, sparePartId),
    {
      enabled: Boolean(sparePartId) && Boolean(companyKey),
      // The ledger only changes when someone books something, and the writers here invalidate
      // this key themselves. A minute keeps a reopened drawer instant without going stale
      // behind a second user's booking for long.
      staleTime: 60000,
    },
  );

  return {
    movements: query.data ?? [],
    // isInitialLoading, not isLoading: a disabled query stays status:"loading" forever in
    // react-query v4, and this one is disabled every moment no part is selected — which is
    // most of them. isLoading would leave a closed drawer showing a skeleton.
    isLoading: query.isInitialLoading,
    isError: query.isError,
    error: query.error,
    isFetching: query.isFetching,
    refetch: query.refetch,
  };
}

export default useStockMovements;
